import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState(() => localStorage.getItem("business_id") || "");

  const loadBusinesses = useCallback(async () => {
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(data);
      // ensure a valid current business is selected
      const stored = localStorage.getItem("business_id");
      const valid = data.find((b) => b.id === stored);
      const next = valid ? stored : (data[0]?.id || "");
      if (next) {
        localStorage.setItem("business_id", next);
        setBusinessId(next);
      }
      return data;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (res) => {
        setUser(res.data);
        await loadBusinesses();
      })
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, [loadBusinesses]);

  const login = async (pin) => {
    const res = await api.post("/auth/login", { pin });
    localStorage.setItem("token", res.data.token);
    const u = res.data.user;
    // a business-scoped user is locked to their own business
    if (u.business_id) localStorage.setItem("business_id", u.business_id);
    setUser(u);
    await loadBusinesses();
    return u;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("business_id");
    setUser(null);
    window.location.href = "/login";
  };

  const switchBusiness = (id) => {
    localStorage.setItem("business_id", id);
    setBusinessId(id);
    window.location.assign("/"); // reload so every page refetches for the new business
  };

  const currentBusiness = businesses.find((b) => b.id === businessId) || null;

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, businesses, businessId, currentBusiness, switchBusiness }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
